import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Store, ArrowLeft, CheckCircle, AlertTriangle, ExternalLink } from 'lucide-react';
import api from '../services/api';
import {
  classifyForgotPasswordError,
  getBuyerForgotPasswordUrl,
  isForgotPasswordEmailFailure,
  sanitizeAuthErrorMessage,
} from '../utils/authErrors';
import { Button, Input } from '../components/ui';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const autoSendRef = useRef(false);

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [emailServiceDown, setEmailServiceDown] = useState(false);

  const buyerSiteUrl =
    process.env.REACT_APP_BUYER_URL?.replace(/\/+$/, '') || 'https://market.st-cael.org';
  const buyerForgotUrl = getBuyerForgotPasswordUrl(buyerSiteUrl);

  useEffect(() => {
    const state = (location.state as { email?: string; autoSend?: boolean } | null) ?? {};
    if (state.email) setEmail(state.email);
    if (state.autoSend && state.email && !autoSendRef.current) {
      autoSendRef.current = true;
    }
  }, [location.state]);

  const sendResetCode = useCallback(
    async (targetEmail: string) => {
      setError('');
      setEmailServiceDown(false);
      setLoading(true);

      try {
        const response = await api.post('/auth/forgot-password', {
          email: targetEmail,
          resetOrigin: window.location.origin,
        });
        if (response.data?.status === 'success') {
          setSubmitted(true);
        } else {
          setError(response.data?.message || t('forgotPassword.errors.failed'));
        }
      } catch (err: unknown) {
        if (isForgotPasswordEmailFailure(err)) {
          setEmailServiceDown(true);
          setError('');
          return;
        }

        const kind = classifyForgotPasswordError(err);
        if (kind === 'not_found') {
          setError(t('forgotPassword.errors.notFound'));
          return;
        }

        const message = sanitizeAuthErrorMessage(
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            (err as Error)?.message
        );
        setError(message || t('forgotPassword.errors.failed'));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    const state = (location.state as { email?: string; autoSend?: boolean } | null) ?? {};
    if (state.autoSend && state.email && autoSendRef.current) {
      autoSendRef.current = false;
      void sendResetCode(state.email);
    }
  }, [location.state, sendResetCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    await sendResetCode(email);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-orange-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            {submitted ? (
              <CheckCircle className="text-green-500" size={48} />
            ) : emailServiceDown ? (
              <AlertTriangle className="text-amber-500" size={48} />
            ) : (
              <Store className="text-custom-primary" size={48} />
            )}
          </div>
          <h1 className="text-4xl font-bold text-custom-primary mb-2">
            {submitted
              ? t('forgotPassword.success.title')
              : emailServiceDown
                ? t('forgotPassword.emailUnavailable.title')
                : t('forgotPassword.title')}
          </h1>
          <p className="text-gray-600">
            {submitted || emailServiceDown || loading ? null : t('forgotPassword.subtitle')}
          </p>
        </div>

        <div className="card p-8">
          {loading && !submitted && !emailServiceDown ? (
            <div className="text-center py-6 space-y-3">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-custom-primary mx-auto" />
              <p className="text-sm text-gray-600">{t('forgotPassword.submitting')}</p>
            </div>
          ) : null}

          {emailServiceDown ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-700 leading-relaxed">
                {t('forgotPassword.emailUnavailable.message')}
              </p>
              {email ? (
                <p className="text-sm text-gray-600">
                  E-mail : <strong>{email}</strong>
                </p>
              ) : null}
              <p className="text-sm text-gray-700">{t('forgotPassword.emailUnavailable.supportHint')}</p>
              <p className="text-sm text-gray-700">{t('forgotPassword.emailUnavailable.buyerHint')}</p>
              <a
                href={buyerForgotUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full rounded-2xl border border-custom-primary text-custom-primary hover:bg-orange-50 font-medium py-3 px-4 transition-colors"
              >
                <ExternalLink size={18} />
                {t('forgotPassword.emailUnavailable.buyerLink')}
              </a>
              <div className="border-t border-gray-200 pt-4 space-y-2">
                <p className="text-sm text-gray-600">{t('forgotPassword.emailUnavailable.hasCode')}</p>
                <Button
                  type="button"
                  variant="primary"
                  className="w-full"
                  onClick={() => navigate('/reset-password', { state: { email } })}
                >
                  {t('forgotPassword.emailUnavailable.enterCode')}
                </Button>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => {
                  setEmailServiceDown(false);
                  if (email) void sendResetCode(email);
                }}
              >
                {t('forgotPassword.tryAgain')}
              </Button>
            </div>
          ) : !submitted && !loading ? (
            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              {error ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              ) : null}

              <Input
                label={t('forgotPassword.emailPlaceholder')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('forgotPassword.emailPlaceholder')}
                icon={Mail}
                iconPosition="left"
                required
              />

              <Button type="submit" variant="primary" className="w-full" loading={loading}>
                {loading ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
              </Button>
            </form>
          ) : submitted ? (
            <div className="space-y-4 text-center">
              <p className="text-gray-600">
                {t('forgotPassword.success.message')}
                <br />
                <strong className="text-gray-900">{email}</strong>
              </p>
              <p className="text-sm text-gray-500">
                {t('forgotPassword.success.checkEmail')}{' '}
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="text-custom-primary hover:underline font-medium"
                >
                  {t('forgotPassword.tryAgain')}
                </button>
              </p>
              <Button
                type="button"
                variant="primary"
                className="w-full"
                onClick={() => navigate('/reset-password', { state: { email } })}
              >
                {t('forgotPassword.success.enterCode')}
              </Button>
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => navigate('/login')}
            className="mt-6 flex items-center justify-center gap-2 w-full text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={16} />
            {t('forgotPassword.backToLogin')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
